/**
 * What a teacher reads when an upload or its reading fails. Pure.
 *
 * Name the actual failure. "Could not read that file" for a storage error sends the teacher
 * off re-exporting a perfectly good PDF — one did exactly that.
 */
import type { QuotaLimits } from './quota';

export interface RunFailure {
  status: number;
  error: string;
  reason?: string;
}

export function uploadErrorMessage(failure: RunFailure, limits: QuotaLimits): string {
  if (failure.status === 0) return 'Something went wrong on our end. Your file is still selected — try again.';
  if (failure.reason === 'content_mismatch') {
    return 'That file does not match its type — a PDF that is not really a PDF, or text that is not UTF-8. Re-export it and try again.';
  }
  if (failure.error === 'quota_exceeded') {
    return `You have used your ${limits.uploads} uploads for the past seven days. A slot opens seven days after each upload.`;
  }
  return 'That file was refused. Try a single lesson plan as a PDF, Word, Markdown or text file.';
}

export function analysisErrorMessage(failure: RunFailure, limits: QuotaLimits): string {
  if (failure.reason === 'profile_required') return 'Set your state, subject and grade first.';
  if (failure.error === 'analysis_in_progress') {
    return 'Another analysis is already running for your account. Please wait for it to finish.';
  }
  if (failure.error === 'quota_exceeded') {
    return `You have used your ${limits.analyses} analyses for the past seven days. A slot opens seven days after each analysis.`;
  }
  if (failure.reason === 'storage_unavailable') {
    return 'We read your plan but could not save the result. Nothing is wrong with your file — try again in a moment.';
  }
  if (failure.reason === 'extraction_unavailable') {
    return 'Reading PDFs is not available right now. A markdown or text export of the same plan will work.';
  }
  return 'We could not read that file. Try a different export of the same plan.';
}

export const REPORT_LOAD_FAILED = 'The reading finished but we could not load it. Try again.';
